<template>
  <div
    class="cta-content"
    :class="[
      `cta-content--${block.text_position}`,
      hasCountdownDate ? 'cta-content--with-countdown' : '',
      block.is_full ? 'full' : '',
      block.has_sibbling ? 'has-sibbling' : '',
    ]"
  >
    <div class="cta-content__text">
      <p
        v-if="hasAccroche"
        class="mb-0-6 text-medium w-600 mb-0-6"
        :class="[
          `text-md-${block.text_position}`,
          `text-color-${block.text_color}`,
        ]"
      >
        {{ block.accroche }}
      </p>

      <p
        v-if="hasTitle"
        class="cta-content__title w-900"
        :class="[
          block.is_full ? 'h1' : 'h3',
          hasCountdownDate || !hasBtn ? 'mb-0-2' : 'mb-1',
          hasCountdownDate ? 'text-center' : '',
          `text-md-${block.text_position}`,
          `text-color-${block.text_color}`,
        ]"
      >
        {{ block.title }}
      </p>

      <div
        v-if="hasBtn"
        class="cta-content__btn"
        :class="[
          block.is_full
            ? 'justify-center'
            : block.text_position === 'right'
            ? 'justify-end'
            : '',

          block.text_position === 'left' ? 'justify-md-start' : '',
          block.text_position === 'center' ? 'justify-md-center' : '',
          block.text_position === 'right' ? 'justify-md-end' : '',
          hasCountdownDate ? 'mt-1 mb-0-6 d-none d-md-flex' : 'd-flex',
        ]"
      >
        <div>
          <primary-button
            ref="link"
            type="button"
            :text="block.btn_label"
            with-arrow
            :aspect="
              block.text_color === 'yellow'
                ? 'yellow'
                : block.is_full
                ? 'dark'
                : 'light'
            "
            :loading="loadingButton"
          />
        </div>
      </div>
    </div>

    <div
      v-if="hasCountdownDate"
      class="cta-content__countdown d-flex d-md-block flex-direction-column align-center mt-1 mt-md-0"
      :class="[`text-color-${block.text_color}`]"
    >
      <div class="d-flex justify-center justify-md-end flex-grow-1 mb-0-6">
        <div class="tag tag--sun-and-white">
          <icon name="estimated-time" />
          <span>{{ $t('product.tag.last_before_end') }}</span>
        </div>
      </div>

      <big-count-down :date="countdownDate" home />
    </div>
  </div>
</template>

<script>
import { isFuture } from 'date-fns'

import PrimaryButton from '@/components/buttons/Primary'
import Icon from '@/components/icons/Icon'

export default {
  name: 'CtaContent',
  components: {
    PrimaryButton,
    Icon,
    BigCountDown: () =>
      import(
        '@/components/countdown/BigCountDown' /* webpackChunkName: 'big_countdown' */
      ),
  },
  props: {
    block: {
      type: Object,
      required: true,
    },
    loadingButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasTitle() {
      return this.block.title && this.block.title.length > 0
    },
    hasAccroche() {
      return this.block.accroche && this.block.accroche.length > 0
    },
    hasCountdownDate() {
      return (
        this.block.countdown_date &&
        isFuture(new Date(this.block.countdown_date))
      )
    },
    hasBtn() {
      return (
        !this.block.btn_hidden &&
        this.block.btn_label &&
        this.block.btn_label.length > 0
      )
    },
    countdownDate() {
      return this.hasCountdownDate ? new Date(this.block.countdown_date) : null
    },
  },
}
</script>

<style lang="scss">
.cta-content {
  position: absolute;
  bottom: calc(var(--spacing) * 2);
  left: calc(var(--spacing) * 2);
  right: calc(var(--spacing) * 2);

  @include mq($from: tablet) {
    bottom: calc(var(--spacing) * 3);
    left: calc(var(--spacing) * 3);
    width: rem(580px);
  }

  .cta-content--right {
    @include mq($from: tablet) {
      left: auto;
      right: calc(var(--spacing) * 3);
      text-align: right;
    }
  }

  &.cta-content--center {
    @include mq($from: tablet) {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  &.full {
    bottom: calc(var(--spacing) * 2);
    left: 0;
    right: 0;
    padding: 0 var(--spacing);
    text-align: center;
    transform: none;

    @include mq($from: tablet) {
      width: auto;
      padding: 0 calc(100% / #{$grid-columns} * 7) 0 calc(var(--spacing) * 2);
      bottom: calc(var(--spacing) * 3);
      text-align: right;
    }

    /* stylelint-disable */
    &:not(.cta-content--with-countdown):not(.has-sibbling) {
      .h1 {
        font-size: var(--h1-font-size);
      }

      &.cta-content--left {
        @include mq($from: tablet) {
          padding: 0 calc(100% / #{$grid-columns} * 7) 0
            calc(var(--spacing) * 2);
          text-align: left;
        }
      }

      &.cta-content--center {
        @include mq($from: tablet) {
          padding: 0 calc(var(--spacing) * 2);
          text-align: center;
        }
      }

      &.cta-content--right {
        @include mq($from: tablet) {
          padding: 0 calc(var(--spacing) * 2) 0
            calc(100% / #{$grid-columns} * 7);
          text-align: right;
        }
      }
    }
    /* stylelint-enable */

    &.cta-content--with-countdown {
      width: auto;
    }
  }

  &.has-sibbling {
    .cta-content__title {
      font-size: var(--h4-font-size);
    }

    @include mq($until: tablet) {
      text-align: left;

      .cta-content__btn {
        justify-content: flex-start;
      }
    }

    @include mq($from: tablet) {
      padding-bottom: calc(var(--spacing) * 1.2);
    }
  }

  &.cta-content--with-countdown {
    @include mq($from: tablet) {
      display: flex;
      justify-content: space-between;
      padding: 0 var(--spacing) var(--spacing);
      right: calc(var(--spacing) * 2);
      bottom: calc(var(--spacing) * 2);
      left: calc(var(--spacing) * 2);
      width: auto;
    }
  }
}
</style>
